import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { saveDraft } from "../api/drafts";

export function useAutoSave(
  draftId: string | null,
  setDraftId: (id: string) => void,
  setStatus: (s: string) => void,
  onSaved?: () => void,
  enabled = true
) {
  const { watch } = useFormContext();
  const timer = useRef<number | null>(null);
  const inFlight = useRef(false);
  const idRef = useRef(draftId);
  useEffect(() => { idRef.current = draftId; }, [draftId]);

  useEffect(() => {
    const sub = watch((values) => {
      if (!enabled) return;
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(async () => {
        if (inFlight.current) return;
        const v = values as Record<string, unknown>;
        const projectName = typeof v.projectName === "string" ? v.projectName.trim() : "";
        const jobNumber = typeof v.jobNumber === "string" ? v.jobNumber.trim() : "";
        if (!projectName || !jobNumber) {
          setStatus(idRef.current ? "Saved" : "Enter project name & job number to save draft");
          return;
        }
        inFlight.current = true;
        setStatus("Saving…");
        try {
          const id = await saveDraft(idRef.current, v);
          if (!idRef.current) setDraftId(id);
          setStatus("Saved " + new Date().toLocaleTimeString());
          onSaved?.();
        } catch {
          setStatus("Save failed");
        } finally {
          inFlight.current = false;
        }
      }, 1500);
    });
    return () => {
      sub.unsubscribe();
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [watch, setDraftId, setStatus, enabled]);
}
